import { Device } from "@capacitor/device";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { getAPIClient } from "./apiClient";
import { errorLog } from "./utils/logger";
import Bugsnag from "@bugsnag/js";
import { updateDevicesAtom } from "./jotai/atomUpdater";

export const registerPushPermission = async () => {
  if (!Capacitor.isNativePlatform()) return;
  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === "prompt") {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive === "granted") {
    await PushNotifications.register();
  }

  return permStatus.receive === "granted";
};

export const initializePushNotificationListeners = async () => {
  if (!Capacitor.isNativePlatform()) return;

  await PushNotifications.addListener("registration", (token) => {
    // Need to explicitly tell Typescript that we are not handling the returned promise
    // since the event listener expects a synchronous function
    void (async () => {
      try {
        const device = await Device.getInfo();
        const apiClient = getAPIClient();
        const devices = await apiClient.getDevices();
        /**
         * Casing of device ID is not guaranteed so compare in a case-insensitive manner.
         */
        const existingDevice = devices.find(
          (device) => device.device_id.toLowerCase() === token.value.toLowerCase()
        );

        if (!existingDevice) {
          const fallbackDeviceName =
            Capacitor.getPlatform() === "ios" ? "iPhone Device" : "Android Device";
          await apiClient.createDevice({
            device_id: token.value,
            // Note on ios 16+ we should just get a generic device name since Apple denied our request for
            // https://developer.apple.com/documentation/bundleresources/entitlements/com.apple.developer.device-information.user-assigned-device-name
            name: device.name || fallbackDeviceName,
            platform: device.platform,
          });
        }
        await updateDevicesAtom();
      } catch (error) {
        errorLog(error as Error);
        Bugsnag.notify(error as Error);
      }
    })();
  });

  await PushNotifications.addListener("registrationError", (error) => {
    errorLog(new Error(error.error));
    Bugsnag.notify(new Error(error.error));
  });
};
